import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Edit Modal Component
const EditModal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-display text-2xl">{title}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
        </div>
        {children}
      </div>
    </div>
  );
};

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingClients, setPendingClients] = useState([]);
  const [approvedClients, setApprovedClients] = useState([]);
  const [wholesaleOrders, setWholesaleOrders] = useState([]);
  const [retailOrders, setRetailOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState({ type: '', text: '' });
  
  // Edit modal states
  const [editingClient, setEditingClient] = useState(null);
  const [editingRetailOrder, setEditingRetailOrder] = useState(null);
  const [editingWholesaleOrder, setEditingWholesaleOrder] = useState(null);

  // Store password in session for API calls
  const [adminPassword, setAdminPassword] = useState(sessionStorage.getItem('admin_password') || '');

  useEffect(() => {
    if (adminPassword) {
      setIsAuthenticated(true);
      fetchAllData();
    }
  }, [adminPassword]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchPendingClients(),
        fetchApprovedClients(),
        fetchWholesaleOrders(),
        fetchRetailOrders(),
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingClients = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/wholesale/pending?password=${adminPassword}`);
      if (response.ok) {
        const data = await response.json();
        setPendingClients(data.pending_clients || []);
      }
    } catch (error) {
      console.error('Error fetching pending clients:', error);
    }
  };

  const fetchApprovedClients = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/wholesale/approved?password=${adminPassword}`);
      if (response.ok) {
        const data = await response.json();
        setApprovedClients(data.approved_clients || []);
      }
    } catch (error) {
      console.error('Error fetching approved clients:', error);
    }
  };

  const fetchWholesaleOrders = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/wholesale/orders?password=${adminPassword}`);
      if (response.ok) {
        const data = await response.json();
        setWholesaleOrders(data.orders || []);
      }
    } catch (error) {
      console.error('Error fetching wholesale orders:', error);
    }
  };

  const fetchRetailOrders = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/orders?password=${adminPassword}`);
      if (response.ok) {
        const data = await response.json();
        setRetailOrders(data.orders || []);
      }
    } catch (error) {
      console.error('Error fetching retail orders:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/wholesale/pending?password=${password}`);
      if (response.ok) {
        setAdminPassword(password);
        sessionStorage.setItem('admin_password', password);
        setIsAuthenticated(true);
      } else {
        setLoginError('Invalid admin password');
      }
    } catch (error) {
      setLoginError('Error connecting to server');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAdminPassword('');
    sessionStorage.removeItem('admin_password');
    setPassword('');
  };

  const showMessage = (type, text) => {
    setActionMessage({ type, text });
    setTimeout(() => setActionMessage({ type: '', text: '' }), 3000);
  };

  const handleApproveClient = async (clientId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/wholesale/approve/${clientId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: adminPassword }),
      });
      
      if (response.ok) {
        showMessage('success', 'Client approved successfully!');
        fetchPendingClients();
        fetchApprovedClients();
      } else {
        showMessage('error', 'Failed to approve client');
      }
    } catch (error) {
      showMessage('error', 'Error approving client');
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/wholesale/clients/${clientId}?password=${adminPassword}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        showMessage('success', 'Client deleted successfully!');
        fetchPendingClients();
        fetchApprovedClients();
      } else {
        showMessage('error', 'Failed to delete client');
      }
    } catch (error) {
      showMessage('error', 'Error deleting client');
    }
  };

  const handleUpdateClient = async (clientId, updateData) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/wholesale/clients/${clientId}?password=${adminPassword}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData),
      });
      
      if (response.ok) {
        showMessage('success', 'Client updated successfully!');
        setEditingClient(null);
        fetchPendingClients();
        fetchApprovedClients();
      } else {
        showMessage('error', 'Failed to update client');
      }
    } catch (error) {
      showMessage('error', 'Error updating client');
    }
  };

  const handleDeleteOrder = async (orderId, isWholesale = false) => {
    if (!window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) {
      return;
    }

    try {
      const endpoint = isWholesale 
        ? `${API_URL}/api/admin/wholesale/orders/${orderId}?password=${adminPassword}`
        : `${API_URL}/api/admin/orders/${orderId}?password=${adminPassword}`;
      
      const response = await fetch(endpoint, { method: 'DELETE' });
      
      if (response.ok) {
        showMessage('success', 'Order deleted successfully!');
        if (isWholesale) {
          fetchWholesaleOrders();
        } else {
          fetchRetailOrders();
        }
      } else {
        showMessage('error', 'Failed to delete order');
      }
    } catch (error) {
      showMessage('error', 'Error deleting order');
    }
  };

  const handleUpdateRetailOrder = async (orderId, updateData) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/orders/${orderId}?password=${adminPassword}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData),
      });
      
      if (response.ok) {
        showMessage('success', 'Order updated successfully!');
        setEditingRetailOrder(null);
        fetchRetailOrders();
      } else {
        showMessage('error', 'Failed to update order');
      }
    } catch (error) {
      showMessage('error', 'Error updating order');
    }
  };

  const handleUpdateWholesaleOrder = async (orderId, updateData) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/wholesale/orders/${orderId}?password=${adminPassword}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData),
      });
      
      if (response.ok) {
        showMessage('success', 'Order updated successfully!');
        setEditingWholesaleOrder(null);
        fetchWholesaleOrders();
      } else {
        showMessage('error', 'Failed to update order');
      }
    } catch (error) {
      showMessage('error', 'Error updating order');
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="paper-texture min-h-screen py-12">
        <div className="max-w-md mx-auto px-6">
          <div className="text-center mb-8">
            <h1 className="font-display text-4xl mb-4">Admin Dashboard</h1>
            <p className="text-[#8C8885]">Enter admin password to continue</p>
          </div>

          <div className="bg-white border border-alt-bg p-8 soft-shadow">
            <form onSubmit={handleLogin} className="space-y-6">
              {loginError && (
                <div className="p-4 bg-red-50 border border-red-200 text-red-700 text-sm">
                  {loginError}
                </div>
              )}

              <div>
                <Label htmlFor="password" className="text-sm font-semibold uppercase tracking-wider mb-2">
                  Admin Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full"
                />
              </div>

              <Button type="submit" className="w-full bg-cape-red hover:bg-red-800 text-white py-3">
                Login
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="paper-texture min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-display text-4xl mb-2">Admin Dashboard</h1>
            <p className="text-[#8C8885]">Manage wholesale clients and orders</p>
          </div>
          <Button onClick={handleLogout} variant="outline" className="text-red-600 border-red-600">
            Logout
          </Button>
        </div>

        {/* Action Message */}
        {actionMessage.text && (
          <div className={`mb-6 p-4 border ${
            actionMessage.type === 'success' 
              ? 'bg-green-50 border-green-200 text-green-700' 
              : 'bg-red-50 border-red-200 text-red-700'
          }`}>
            {actionMessage.text}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white border border-alt-bg p-6 text-center">
            <p className="text-3xl font-bold text-cape-red">{pendingClients.length}</p>
            <p className="text-sm text-[#8C8885] uppercase">Pending Approvals</p>
          </div>
          <div className="bg-white border border-alt-bg p-6 text-center">
            <p className="text-3xl font-bold text-green-600">{approvedClients.length}</p>
            <p className="text-sm text-[#8C8885] uppercase">Wholesale Clients</p>
          </div>
          <div className="bg-white border border-alt-bg p-6 text-center">
            <p className="text-3xl font-bold text-blue-600">{wholesaleOrders.length}</p>
            <p className="text-sm text-[#8C8885] uppercase">Wholesale Orders</p>
          </div>
          <div className="bg-white border border-alt-bg p-6 text-center">
            <p className="text-3xl font-bold text-purple-600">{retailOrders.length}</p>
            <p className="text-sm text-[#8C8885] uppercase">Retail Orders</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-alt-bg overflow-x-auto">
          {['pending', 'clients', 'wholesale-orders', 'retail-orders'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-4 px-4 font-semibold whitespace-nowrap ${
                activeTab === tab ? 'border-b-2 border-cape-red text-cape-red' : 'text-[#8C8885]'
              }`}
            >
              {tab === 'pending' && `Pending Approvals (${pendingClients.length})`}
              {tab === 'clients' && `Wholesale Clients (${approvedClients.length})`}
              {tab === 'wholesale-orders' && `Wholesale Orders (${wholesaleOrders.length})`}
              {tab === 'retail-orders' && `Retail Orders (${retailOrders.length})`}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12 text-[#8C8885]">Loading...</div>
        ) : (
          <>
            {/* Pending Approvals Tab */}
            {activeTab === 'pending' && (
              <div className="bg-white border border-alt-bg">
                {pendingClients.length === 0 ? (
                  <div className="p-8 text-center text-[#8C8885]">No pending wholesale registrations</div>
                ) : (
                  <div className="divide-y divide-alt-bg">
                    {pendingClients.map((client) => (
                      <div key={client.id} className="p-6">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-bold text-lg">{client.company_name}</h3>
                            <p className="text-[#8C8885] text-sm">{client.contact_name}</p>
                            <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                              <div><span className="text-[#8C8885]">Email:</span> {client.email}</div>
                              <div><span className="text-[#8C8885]">Phone:</span> {client.phone}</div>
                              <div className="col-span-2">
                                <span className="text-[#8C8885]">Address:</span> {client.address}, {client.city}, {client.state} {client.zip_code}
                              </div>
                              {client.tax_id && <div><span className="text-[#8C8885]">Tax ID:</span> {client.tax_id}</div>}
                              <div><span className="text-[#8C8885]">Registered:</span> {new Date(client.created_at).toLocaleDateString()}</div>
                            </div>
                            {client.notes && (
                              <div className="mt-2 p-2 bg-alt-bg text-sm">
                                <span className="text-[#8C8885]">Notes:</span> {client.notes}
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col gap-2 ml-4">
                            <Button onClick={() => handleApproveClient(client.id)} className="bg-green-600 hover:bg-green-700 text-white">
                              Approve
                            </Button>
                            <Button onClick={() => setEditingClient(client)} variant="outline">
                              Edit
                            </Button>
                            <Button onClick={() => handleDeleteClient(client.id)} variant="outline" className="text-red-600 border-red-600">
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Approved Clients Tab */}
            {activeTab === 'clients' && (
              <div className="bg-white border border-alt-bg">
                {approvedClients.length === 0 ? (
                  <div className="p-8 text-center text-[#8C8885]">No approved wholesale clients yet</div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-alt-bg">
                      <tr>
                        <th className="text-left p-4 font-semibold text-sm uppercase">Company</th>
                        <th className="text-left p-4 font-semibold text-sm uppercase">Contact</th>
                        <th className="text-left p-4 font-semibold text-sm uppercase">Email</th>
                        <th className="text-left p-4 font-semibold text-sm uppercase">Phone</th>
                        <th className="text-center p-4 font-semibold text-sm uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {approvedClients.map((client) => (
                        <tr key={client.id} className="border-t border-alt-bg">
                          <td className="p-4 font-semibold">{client.company_name}</td>
                          <td className="p-4">{client.contact_name}</td>
                          <td className="p-4">{client.email}</td>
                          <td className="p-4">{client.phone}</td>
                          <td className="p-4 text-center">
                            <div className="flex gap-2 justify-center">
                              <Button onClick={() => setEditingClient(client)} variant="outline" size="sm">
                                Edit
                              </Button>
                              <Button onClick={() => handleDeleteClient(client.id)} variant="outline" size="sm" className="text-red-600 border-red-600">
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Wholesale Orders Tab */}
            {activeTab === 'wholesale-orders' && (
              <div className="bg-white border border-alt-bg">
                {wholesaleOrders.length === 0 ? (
                  <div className="p-8 text-center text-[#8C8885]">No wholesale orders yet</div>
                ) : (
                  <div className="divide-y divide-alt-bg">
                    {wholesaleOrders.map((order) => (
                      <div key={order.id} className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="font-bold">{order.company_name}</h3>
                            <p className="text-sm text-[#8C8885]">
                              Order #{order.id.slice(0, 8)} • {new Date(order.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-xl text-cape-red">${order.subtotal.toFixed(2)}</p>
                            <span className={`inline-block mt-1 px-3 py-1 rounded text-sm font-semibold ${
                              order.status === 'completed' ? 'bg-green-100 text-green-700' :
                              order.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                              order.status === 'shipped' ? 'bg-purple-100 text-purple-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                            </span>
                            <div className="mt-2 flex gap-2 justify-end">
                              <Button onClick={() => setEditingWholesaleOrder(order)} variant="outline" size="sm">
                                Edit
                              </Button>
                              <Button onClick={() => handleDeleteOrder(order.id, true)} variant="outline" size="sm" className="text-red-600 border-red-600">
                                Delete
                              </Button>
                            </div>
                          </div>
                        </div>
                        <div className="text-sm">
                          <p className="text-[#8C8885] mb-2">Items:</p>
                          <ul className="list-disc list-inside">
                            {order.items.map((item, idx) => (
                              <li key={idx}>
                                {item.name} × {item.quantity} @ ${item.wholesale_price.toFixed(2)} = ${(item.wholesale_price * item.quantity).toFixed(2)}
                              </li>
                            ))}
                          </ul>
                          {order.notes && (
                            <p className="mt-2 p-2 bg-alt-bg"><span className="text-[#8C8885]">Notes:</span> {order.notes}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Retail Orders Tab */}
            {activeTab === 'retail-orders' && (
              <div className="bg-white border border-alt-bg">
                {retailOrders.length === 0 ? (
                  <div className="p-8 text-center text-[#8C8885]">No retail orders yet</div>
                ) : (
                  <div className="divide-y divide-alt-bg">
                    {retailOrders.map((order) => (
                      <div key={order.id} className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="font-bold">{order.customerName}</h3>
                            <p className="text-sm text-[#8C8885]">{order.customerEmail}</p>
                            <p className="text-sm text-[#8C8885]">Order #{order.id.slice(0, 8)} • {new Date(order.createdAt).toLocaleDateString()}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-xl text-cape-red">${order.total.toFixed(2)}</p>
                            <span className={`inline-block mt-1 px-3 py-1 rounded text-sm font-semibold ${
                              order.status === 'completed' ? 'bg-green-100 text-green-700' :
                              order.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                              order.status === 'shipped' ? 'bg-purple-100 text-purple-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                            </span>
                            <div className="mt-2 flex gap-2 justify-end">
                              <Button onClick={() => setEditingRetailOrder(order)} variant="outline" size="sm">
                                Edit
                              </Button>
                              <Button onClick={() => handleDeleteOrder(order.id, false)} variant="outline" size="sm" className="text-red-600 border-red-600">
                                Delete
                              </Button>
                            </div>
                          </div>
                        </div>
                        <div className="text-sm grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-[#8C8885]">Shipping Address:</p>
                            <p>{order.shippingAddress}</p>
                          </div>
                          <div>
                            <p className="text-[#8C8885]">Order Summary:</p>
                            <p>Subtotal: ${order.subtotal.toFixed(2)}</p>
                            <p>Shipping: ${order.shipping.toFixed(2)}</p>
                            <p>Tax: ${order.tax.toFixed(2)}</p>
                          </div>
                        </div>
                        <div className="mt-2 text-sm">
                          <p className="text-[#8C8885]">Items:</p>
                          <ul className="list-disc list-inside">
                            {order.items.map((item, idx) => (
                              <li key={idx}>{item.name} × {item.quantity} @ ${item.price.toFixed(2)}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Refresh Button */}
        <div className="mt-6 text-center">
          <Button onClick={fetchAllData} variant="outline" disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh Data'}
          </Button>
        </div>
      </div>

      {/* Edit Client Modal */}
      <EditModal
        isOpen={!!editingClient}
        onClose={() => setEditingClient(null)}
        title="Edit Client"
      >
        {editingClient && (
          <ClientEditForm
            client={editingClient}
            onSave={(data) => handleUpdateClient(editingClient.id, data)}
            onCancel={() => setEditingClient(null)}
          />
        )}
      </EditModal>

      {/* Edit Retail Order Modal */}
      <EditModal
        isOpen={!!editingRetailOrder}
        onClose={() => setEditingRetailOrder(null)}
        title="Edit Retail Order"
      >
        {editingRetailOrder && (
          <RetailOrderEditForm
            order={editingRetailOrder}
            onSave={(data) => handleUpdateRetailOrder(editingRetailOrder.id, data)}
            onCancel={() => setEditingRetailOrder(null)}
          />
        )}
      </EditModal>

      {/* Edit Wholesale Order Modal */}
      <EditModal
        isOpen={!!editingWholesaleOrder}
        onClose={() => setEditingWholesaleOrder(null)}
        title="Edit Wholesale Order"
      >
        {editingWholesaleOrder && (
          <WholesaleOrderEditForm
            order={editingWholesaleOrder}
            onSave={(data) => handleUpdateWholesaleOrder(editingWholesaleOrder.id, data)}
            onCancel={() => setEditingWholesaleOrder(null)}
          />
        )}
      </EditModal>
    </div>
  );
};

// Client Edit Form Component
const ClientEditForm = ({ client, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    company_name: client.company_name || '',
    contact_name: client.contact_name || '',
    phone: client.phone || '',
    address: client.address || '',
    city: client.city || '',
    state: client.state || '',
    zip_code: client.zip_code || '',
    tax_id: client.tax_id || '',
    notes: client.notes || '',
    approved: client.approved || false,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-sm font-semibold uppercase">Company Name</Label>
          <Input
            value={formData.company_name}
            onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
            required
          />
        </div>
        <div>
          <Label className="text-sm font-semibold uppercase">Contact Name</Label>
          <Input
            value={formData.contact_name}
            onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
            required
          />
        </div>
        <div>
          <Label className="text-sm font-semibold uppercase">Phone</Label>
          <Input
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            required
          />
        </div>
        <div>
          <Label className="text-sm font-semibold uppercase">Tax ID</Label>
          <Input
            value={formData.tax_id}
            onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
          />
        </div>
        <div className="col-span-2">
          <Label className="text-sm font-semibold uppercase">Address</Label>
          <Input
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            required
          />
        </div>
        <div>
          <Label className="text-sm font-semibold uppercase">City</Label>
          <Input
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            required
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-sm font-semibold uppercase">State</Label>
            <Input
              value={formData.state}
              onChange={(e) => setFormData({ ...formData, state: e.target.value })}
              required
            />
          </div>
          <div>
            <Label className="text-sm font-semibold uppercase">ZIP</Label>
            <Input
              value={formData.zip_code}
              onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
              required
            />
          </div>
        </div>
        <div className="col-span-2">
          <Label className="text-sm font-semibold uppercase">Notes</Label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            className="w-full p-2 border border-input rounded"
            rows={2}
          />
        </div>
        <div className="col-span-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.approved}
              onChange={(e) => setFormData({ ...formData, approved: e.target.checked })}
              className="w-4 h-4"
            />
            <span className="font-semibold">Approved</span>
          </label>
        </div>
      </div>
      <div className="flex gap-4 justify-end pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" className="bg-cape-red hover:bg-red-800 text-white">Save Changes</Button>
      </div>
    </form>
  );
};

// Retail Order Edit Form Component
const RetailOrderEditForm = ({ order, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    customerName: order.customerName || '',
    customerEmail: order.customerEmail || '',
    customerPhone: order.customerPhone || '',
    shippingAddress: order.shippingAddress || '',
    status: order.status || 'pending',
    notes: order.notes || '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-sm font-semibold uppercase">Customer Name</Label>
          <Input
            value={formData.customerName}
            onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
            required
          />
        </div>
        <div>
          <Label className="text-sm font-semibold uppercase">Email</Label>
          <Input
            type="email"
            value={formData.customerEmail}
            onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
            required
          />
        </div>
        <div>
          <Label className="text-sm font-semibold uppercase">Phone</Label>
          <Input
            value={formData.customerPhone}
            onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
          />
        </div>
        <div>
          <Label className="text-sm font-semibold uppercase">Status</Label>
          <select
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            className="w-full p-2 border border-input rounded"
          >
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="shipped">Shipped</option>
            <option value="completed">Completed</option>
          </select>
        </div>
        <div className="col-span-2">
          <Label className="text-sm font-semibold uppercase">Shipping Address</Label>
          <Input
            value={formData.shippingAddress}
            onChange={(e) => setFormData({ ...formData, shippingAddress: e.target.value })}
            required
          />
        </div>
        <div className="col-span-2">
          <Label className="text-sm font-semibold uppercase">Notes</Label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            className="w-full p-2 border border-input rounded"
            rows={2}
          />
        </div>
      </div>
      <div className="flex gap-4 justify-end pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" className="bg-cape-red hover:bg-red-800 text-white">Save Changes</Button>
      </div>
    </form>
  );
};

// Wholesale Order Edit Form Component
const WholesaleOrderEditForm = ({ order, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    status: order.status || 'pending',
    notes: order.notes || '',
    shipping_address: order.shipping_address || '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="mb-4 p-4 bg-alt-bg rounded">
        <p className="font-semibold">{order.company_name}</p>
        <p className="text-sm text-[#8C8885]">Order #{order.id.slice(0, 8)}</p>
        <p className="text-sm font-bold mt-2">Total: ${order.subtotal.toFixed(2)}</p>
      </div>
      <div>
        <Label className="text-sm font-semibold uppercase">Status</Label>
        <select
          value={formData.status}
          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
          className="w-full p-2 border border-input rounded"
        >
          <option value="pending">Pending</option>
          <option value="processing">Processing</option>
          <option value="shipped">Shipped</option>
          <option value="completed">Completed</option>
        </select>
      </div>
      <div>
        <Label className="text-sm font-semibold uppercase">Shipping Address</Label>
        <Input
          value={formData.shipping_address}
          onChange={(e) => setFormData({ ...formData, shipping_address: e.target.value })}
          placeholder="Override shipping address (optional)"
        />
      </div>
      <div>
        <Label className="text-sm font-semibold uppercase">Notes</Label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          className="w-full p-2 border border-input rounded"
          rows={3}
        />
      </div>
      <div className="flex gap-4 justify-end pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" className="bg-cape-red hover:bg-red-800 text-white">Save Changes</Button>
      </div>
    </form>
  );
};

export default AdminPage;
